import React, { createContext, useContext, useEffect } from "react";
import { useSelector } from "react-redux";

/**
 * AccessProvider
 * ----------------
 * Wrap your application with this provider to automatically hide DOM elements
 * based on role permissions. Any element that should be protected must include
 * an id that starts with `access_`. Example:
 *
 * ```jsx
 * <Button id="access_people_create">New Contact</Button>
 * ```
 *
 * Utility hooks exposed:
 * - `hasPageAccess(id)`     -> check access id from `pages_id` list
 * - `hasModuleAccess(id)`   -> check sidebar/module access
 * - `hasPathAccess(path)`   -> check if a route is accessible
 *
 * During development you can enable a highlight mode by setting
 * `localStorage.setItem('accessDebug', 'true')`. Allowed elements will be
 * outlined in green and restricted ones in red.
 */

const AccessContext = createContext();

export const AccessProvider = ({ children }) => {
  const { userAccess, token, lawerId } = useSelector((state) => state.user);
  const roles =
    Array.isArray(userAccess) && userAccess.length > 0
      ? userAccess
      : noAccessProvide;
  const debug = localStorage.getItem("accessDebug") === "true";

  // const refreshAccess = async () => {
  //   if (!token || !lawerId) return;
  //   try {
  //     const res = await axios.get(
  //       `${backend_api}team/create-employee/${lawerId}/`,
  //       {
  //         headers: { Authorization: `Token ${token}` },
  //       }
  //     );

  //     const team = res?.data;
  //     if (team) {
  //       const merged = [];
  //       const ids = new Set();
  //       (team.access || []).forEach((role) => {
  //         if (role && !ids.has(role.id)) {
  //           ids.add(role.id);
  //           merged.push(role);
  //         }
  //       });
  //       dispatch(setUserAccess(merged.length > 0 ? merged : noAccessProvide));
  //       if (team?.lawyer?.id) {
  //         dispatch(setTeamMemberLawyerId(team.lawyer.id));
  //       }
  //     }
  //   } catch (err) {
  //     console.error("[AccessProvider] Failed to refresh access", err);
  //   }
  // };

  // useEffect(() => {
  //   refreshAccess();
  // }, [location.pathname]);

  // useEffect(() => {
  //   refreshAccess();
  //   const interval = setInterval(refreshAccess, 60000);
  //   return () => clearInterval(interval);
  // }, [token, lawerId]);

  useEffect(() => {
    if (!roles || !Array.isArray(roles)) return;

    const hasHighAccess = roles.some((role) => role.direct_login === true);

    const collectAllowedIds = () => {
      const allowedIds = [];
      roles.forEach((role) => {
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allowedIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });
      return [...new Set(allowedIds)];
    };

    const applyAccessControl = () => {
      const allowed = collectAllowedIds();
      const elements = document.querySelectorAll('[id^="access_"]');
      elements.forEach((el) => {
        const allowedElement = hasHighAccess || allowed.includes(el.id);
        el.style.display = allowedElement ? "" : "none";
        if (debug) {
          el.style.outline = allowedElement
            ? "2px solid green"
            : "2px solid red";
        }
      });
    };

    applyAccessControl();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (hasHighAccess) {
              if (node.id && node.id.startsWith("access_")) {
                node.style.display = "";
                if (debug) node.style.outline = "2px solid green";
              }
              const childElements = node.querySelectorAll('[id^="access_"]');
              childElements.forEach((child) => {
                child.style.display = "";
                if (debug) child.style.outline = "2px solid green";
              });
              return;
            }

            const uniqueAllowedIds = collectAllowedIds();

            if (node.id && node.id.startsWith("access_")) {
              const allowedElement = uniqueAllowedIds.includes(node.id);
              node.style.display = allowedElement ? "" : "none";
              if (debug) {
                node.style.outline = allowedElement
                  ? "2px solid green"
                  : "2px solid red";
              }
            }

            const childElements = node.querySelectorAll('[id^="access_"]');
            childElements.forEach((child) => {
              const allowedChild = uniqueAllowedIds.includes(child.id);
              child.style.display = allowedChild ? "" : "none";
              if (debug) {
                child.style.outline = allowedChild
                  ? "2px solid green"
                  : "2px solid red";
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [roles]);
  const normalizePath = (p) =>
    p
      ?.replace(/\/:\w+/g, "")
      .replace(/\*/g, "")
      .replace(/\/$/, "");

  const matchesPath = (allowed, target) => {
    const a = normalizePath(allowed);
    const t = normalizePath(target);
    return t === a || t.startsWith(`${a}/`);
  };

  const value = {
    userAccess: roles,
    canNavigate: (path) => {
      if (!roles || !Array.isArray(roles)) return false;
      if (roles.some((role) => role.direct_login && role.direct_login === true))
        return true;
      return roles.some((role) =>
        role.custom_access?.modules_path?.some((p) => matchesPath(p, path)),
      );
    },

    hasAccess: (permission) => {
      if (!roles || !Array.isArray(roles)) return false;
      if (roles.some((role) => role.direct_login && role.direct_login === true))
        return true;
      return roles.some((role) =>
        role.custom_access?.modules_id?.includes(permission),
      );
    },

    isHighAccess: () => {
      if (!roles || !Array.isArray(roles)) return false;
      return roles.some((role) => role.direct_login === true);
    },

    hasModuleAccess: (moduleId) => {
      if (!roles || !Array.isArray(roles)) return false;

      if (roles.some((role) => role.direct_login && role.direct_login === true))
        return true;

      return roles.some((role) =>
        role.custom_access?.modules_id?.includes(moduleId),
      );
    },

    hasPathAccess: (path) => {
      if (!roles || !Array.isArray(roles)) return false;

      if (roles.some((role) => role.direct_login && role.direct_login === true))
        return true;

      return roles.some((role) =>
        role.custom_access?.modules_path?.some((p) => matchesPath(p, path)),
      );
    },

    getAllowedPaths: () => {
      if (!roles || !Array.isArray(roles)) return [];

      if (roles.some((role) => role.direct_login && role.direct_login === true))
        return true;

      const allPaths = [];
      roles.forEach((role) => {
        if (role.custom_access?.modules_path) {
          allPaths.push(...role.custom_access.modules_path);
        }
      });
      return [...new Set(allPaths)]; // Remove duplicates
    },
    getAllowedModules: () => {
      if (!roles || !Array.isArray(roles)) return [];

      if (roles.some((role) => role.direct_login && role.direct_login === true))
        return true;

      const allModules = [];
      roles.forEach((role) => {
        if (role.custom_access?.modules_id) {
          allModules.push(
            ...role.custom_access.modules_id.filter((id) => id !== null),
          );
        }
      });
      return [...new Set(allModules)]; // Remove duplicates
    },
    getAllowedPageIds: () => {
      if (!roles || !Array.isArray(roles)) return [];

      if (roles.some((role) => role.direct_login === true)) return ["*"];

      const allowedIds = [];
      roles.forEach((role) => {
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allowedIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });
      return [...new Set(allowedIds)]; // Remove duplicates
    },
    hasPageAccess: (pageId) => {
      if (!roles || !Array.isArray(roles)) return false;

      if (roles.some((role) => role.direct_login === true)) return true;

      return roles.some((role) => {
        if (role.custom_access?.pages_id) {
          return Object.values(role.custom_access.pages_id).some(
            (moduleIds) =>
              Array.isArray(moduleIds) && moduleIds.includes(pageId),
          );
        }
        return false;
      });
    },

    hasAnyAccess: (items) => {
      if (!roles || !Array.isArray(roles)) return false;

      if (roles.some((role) => role.direct_login === true)) return true;

      // Get all allowed items from all roles
      const allAllowedPaths = [];
      const allAllowedModules = [];
      const allAllowedPageIds = [];

      roles.forEach((role) => {
        if (role.custom_access?.modules_path) {
          allAllowedPaths.push(...role.custom_access.modules_path);
        }
        if (role.custom_access?.modules_id) {
          allAllowedModules.push(
            ...role.custom_access.modules_id.filter((id) => id !== null),
          );
        }
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allAllowedPageIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });

      return items.some(
        (item) =>
          allAllowedPaths.includes(item) ||
          allAllowedModules.includes(item) ||
          allAllowedPageIds.includes(item),
      );
    },

    // New method to check if an element should be hidden
    shouldHideElement: (elementId) => {
      if (!roles || !Array.isArray(roles)) return true;

      if (roles.some((role) => role.direct_login === true)) return false;

      const allowedIds = [];
      roles.forEach((role) => {
        if (role.custom_access?.pages_id) {
          Object.values(role.custom_access.pages_id).forEach((moduleIds) => {
            if (Array.isArray(moduleIds)) {
              allowedIds.push(...moduleIds.filter((id) => id !== null));
            }
          });
        }
      });

      const uniqueAllowedIds = [...new Set(allowedIds)];
      return !uniqueAllowedIds.includes(elementId);
    },
  };

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
};

export const useAccess = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error("useAccess must be used within an AccessProvider");
  }
  return context;
};

export const noAccessProvide = [
  {
    role_name: "default access",
    description:
      "this access will use when user is invited but don't access is provided",
    custom_access: {
      pages_id: {
        task_module: [
          "access_new_task_btn",
          "access_new_task_edit_btn",
          "access_new_task_del_btn",
          "access_assign_to_other",
          "access_private_task_input",
          "access_add_task_list",
          "access_edit_task_list",
          "access_delete_task_list",
          "access_add_task_type",
          "access_edit_task_type",
          "access_delete_task_type",
          "access_task_feed_comment",
        ],
        matters_module: [
          "access_matters_create",
          "access_matters_edit",
          "access_matters_delete",
          "access_matters_close",
          "access_matters_archive",
          "access_case_dashboard",
          "access_matter_stages_create",
          "access_matter_stages_edit",
          "access_matter_stages_delete",
          "access_matter_templates_create",
          "access_matter_templates_edit",
          "access_matter_templates_delete",
        ],
        calendar_module: [
          "access_calendar_create",
          "access_calendar_edit",
          "access_calendar_delete",
          "access_calendar_manage_others",
        ],
        people_module: [
          "access_people_create",
          "access_people_edit",
          "access_people_delete",
          "access_people_export",
          "access_people_import",
          "access_people_tags",
        ],
        company_module: [
          "access_company_create",
          "access_company_edit",
          "access_company_delete",
          "access_company_export",
          "access_company_import",
          "access_company_tags",
        ],
        documents_module: [
          "access_documents_upload",
          "access_documents_download",
          "access_documents_edit",
          "access_documents_delete",
          "access_documents_share",
          "access_document_templates_create",
          "access_document_templates_edit",
          "access_document_templates_delete",
          "access_document_categories_create",
          "access_document_categories_edit",
          "access_document_categories_delete",
        ],
        activities_module: [
          "access_activities_create",
          "access_activities_edit",
          "access_activities_delete",
          "access_activities_log_time",
          "access_activities_add_exp",
          "access_activities_add_exp_categories",
        ],
        integrations_module: [
          "access_integrations_configure",
          "access_integrations_connect",
          "access_integrations_disconnect",
        ],
        custom_fields_module: [
          "access_custom_fields_create",
          "access_custom_fields_edit",
          "access_custom_fields_delete",
        ],
        communications_module: [
          "access_communications_send",
          "access_communications_email",
          "access_communications_sms",
        ],
        dashboard_module: [
          "access_firm_dashboard_revenue_chart",
          "access_firm_dashboard_matter_status_chart",
          "access_firm_dashboard_practice_area_chart",
          "access_firm_dashboard_top_users_chart",
          "access_firm_dashboard_recent_activity",
          "access_firm_dashboard_graph3",
        ],
      },
      modules_id: [
        "sidebar_dashboard",
        "sidebar_calendar",
        "sidebar_tasks_flow",
        "sidebar_tasks",
        "sidebar_task_template",
        "sidebar_task_group_tasks",
        "sidebar_matters",
        "sidebar_all_matters",
        "sidebar_matter_templates",
        "sidebar_people",
        "sidebar_companies",
        "sidebar_activities",
        "sidebar_billing",
        "sidebar_accounts",
        "sidebar_documents",
        "sidebar_documents_main",
        "sidebar_document_templates",
        "sidebar_communications",
        "sidebar_settings",
      ],
      modules_path: [
        "/dashboard",
        "/dashboard/calendars",
        "/dashboard/tasks",
        "/dashboard/task/template",
        "/dashboard/task/group-tasks",
        "/dashboard/task/lists/:id",
        "/dashboard/matter",
        "/dashboard/matter/:id",
        "/dashboard/matter-template",
        "/dashboard/people",
        "/dashboard/people/:id",
        "/dashboard/companies",
        "/dashboard/companies/:id",
        "/dashboard/activities",
        "/dashboard/bills",
        "/dashboard/bank_accounts",
        "/dashboard/bank_accounts/:id/:accountName",
        "/dashboard/documents",
        "/dashboard/documents/templates",
        "/dashboard/communications",
        "/dashboard/settings",
        "/dashboard/settings/*",
        "/dashboard/ai",
        "/dashboard/ai-workspace",
      ],
      matter_restricted_access: false,
    },
  },
];
